import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Checkbox,
    IconButton,
    TextField,
    CircularProgress,
    Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

// Chapter picker for MANUAL_TARGET / FIXED_SET rounds (Phase 3). Renders the ETT taxonomy as
// its real TREE (the taxonomy endpoint passes parent_qb_chapter_id / is_parent through).
// - allowParents (FIXED_SET): a parent is pickable — the server samples across its subtree.
// - showTargets (MANUAL_TARGET): per-leaf "questions" override next to each picked leaf.
// value: [{ chapter_id, total_questions? }]; onChange(next) replaces it.
const ManualChapterPicker = ({ qbType, subjectId, value, onChange, allowParents = false, showTargets = false }) => {
    const [tax, setTax] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState({}); // chapter_id -> expanded

    useEffect(() => {
        if (!qbType) return;
        setLoading(true);
        Axios.get(`${BASE}/taxonomy?qb_type=${qbType}`, { headers: { Authorization: getAuthHeader() } })
            .then((res) => setTax(res.data || []))
            .catch(() => setTax(null))
            .finally(() => setLoading(false));
    }, [qbType]);

    const picked = useMemo(() => new Map((value || []).map((v) => [v.chapter_id, v])), [value]);

    const { roots, childrenOf, byId } = useMemo(() => {
        const childrenOfM = new Map();
        const byIdM = new Map();
        const rootsA = [];
        (tax || []).forEach((s) => {
            // Scope to the round's subject when the taxonomy labels subjects and one is given.
            if (subjectId != null && s.subject_id != null && s.subject_id !== subjectId) return;
            (s.chapters || []).forEach((ch) => {
                byIdM.set(ch.id, ch);
                if (ch.parent_qb_chapter_id == null) rootsA.push(ch.id);
                else {
                    if (!childrenOfM.has(ch.parent_qb_chapter_id)) childrenOfM.set(ch.parent_qb_chapter_id, []);
                    childrenOfM.get(ch.parent_qb_chapter_id).push(ch.id);
                }
            });
        });
        return { roots: rootsA, childrenOf: childrenOfM, byId: byIdM };
    }, [tax, subjectId]);

    const toggle = (ch) => {
        const next = new Map(picked);
        if (next.has(ch.id)) next.delete(ch.id);
        else next.set(ch.id, { chapter_id: ch.id });
        onChange([...next.values()]);
    };
    const setTarget = (chapterId, raw) => {
        const next = new Map(picked);
        const entry = next.get(chapterId);
        if (!entry) return;
        next.set(chapterId, { ...entry, total_questions: raw === "" ? undefined : Number(raw) });
        onChange([...next.values()]);
    };

    const renderNode = (id, depth) => {
        const ch = byId.get(id);
        if (!ch) return null;
        const kids = childrenOf.get(id) || [];
        const isParent = ch.is_parent === true || kids.length > 0;
        // Tree ROOTS are chapters ("Ch 1…"), not subjects — the taxonomy payload already
        // groups by subject, so parents are pickable at every depth when allowed.
        const pickable = isParent ? allowParents : (ch.question_count ?? 0) > 0;
        const isPicked = picked.has(ch.id);
        return (
            <Box key={id} sx={{ ml: depth * 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {kids.length > 0 ? (
                        <IconButton size="small" onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))} aria-label="Expand chapter">
                            {open[id] ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                    ) : <Box sx={{ width: 28 }} />}
                    <Checkbox size="small" checked={isPicked} disabled={!pickable} onChange={() => toggle(ch)} />
                    <Typography variant="body2" sx={{ opacity: pickable ? 1 : 0.5 }}>
                        {ch.title}
                        {!isParent && <Typography component="span" variant="caption" color="text.secondary"> · {ch.question_count ?? 0} q</Typography>}
                        {isParent && allowParents && (
                            <Typography component="span" variant="caption" color="text.secondary"> · whole chapter</Typography>
                        )}
                    </Typography>
                    {showTargets && isPicked && !isParent && (
                        <TextField
                            size="small" type="number" label="Questions" sx={{ width: 110, ml: 1 }}
                            value={picked.get(ch.id).total_questions ?? ""}
                            onChange={(e) => setTarget(ch.id, e.target.value)}
                        />
                    )}
                </Box>
                {open[id] && kids.map((kid) => renderNode(kid, depth + 1))}
            </Box>
        );
    };

    if (loading) return <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>;
    if (!tax) return <Alert severity="error">Couldn't load the chapter list from ETT — try again.</Alert>;

    return (
        <Box sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {allowParents
                    ? "Tick a whole chapter (questions are drawn across all of it), or expand ▸ to pick specific sub-chapters."
                    : "Expand ▸ a chapter and tick the sub-chapters to assign (targets are per sub-chapter)."}
            </Typography>
            {roots.map((id) => renderNode(id, 0))}
            {(value || []).length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {(value || []).length} chapter{(value || []).length === 1 ? "" : "s"} selected
                </Typography>
            )}
        </Box>
    );
};

export default ManualChapterPicker;
